import { useEffect, useRef } from "react";

/**
 * Pegar una captura con Ctrl+V.
 *
 * En un ordenador, la captura recien tomada ya esta en el portapapeles: pedir
 * que se guarde en un archivo, se busque en una carpeta y se elija en un
 * selector son tres pasos para nada. Esto los quita.
 *
 * Dos reglas que no se negocian:
 *
 *   1. **Un pegado de TEXTO no se toca.** Solo se interviene si el portapapeles
 *      trae imagenes, asi que Ctrl+V dentro de un area de texto sigue pegando
 *      texto, que es su uso normal en estas pantallas.
 *   2. **Se escucha en la ventana, no en un campo.** Nadie va a enfocar una
 *      zona de arrastre antes de pegar; el gesto es abrir la pantalla y pulsar
 *      Ctrl+V.
 *
 * Si el navegador no deja leer el portapapeles, o el pegado no trae ninguna
 * imagen, no pasa nada: se ignora y el evento sigue su camino.
 */

/**
 * Las imagenes de un pegado o de un arrastre. `files` es lo que llena la
 * herramienta de recortes de Windows y casi todos los navegadores; `items` es
 * el respaldo, porque hay versiones que llenan uno y no el otro.
 */
export function imagesFromClipboard(data: DataTransfer | null): File[] {
  if (data === null) return [];

  const found: File[] = [];

  for (const file of data.files) {
    if (file.type.startsWith("image/")) found.push(file);
  }

  if (found.length > 0) return found;

  for (let at = 0; at < data.items.length; at += 1) {
    const item = data.items[at];

    if (item === undefined || item.kind !== "file" || !item.type.startsWith("image/")) continue;

    const file = item.getAsFile();

    if (file !== null) found.push(file);
  }

  return found;
}

export function usePasteImages(
  enabled: boolean,
  onImages: (files: readonly File[]) => void,
): void {
  /* La funcion viaja en un ref para que el oyente se ponga y se quite cuando
     cambia `enabled`, y no en cada repintado del formulario. */
  const sink = useRef(onImages);

  useEffect(() => {
    sink.current = onImages;
  });

  useEffect(() => {
    if (!enabled) return;

    const onPaste = (event: ClipboardEvent): void => {
      const images = imagesFromClipboard(event.clipboardData);

      if (images.length === 0) return;

      event.preventDefault();
      sink.current(images);
    };

    window.addEventListener("paste", onPaste);

    return () => window.removeEventListener("paste", onPaste);
  }, [enabled]);
}
